import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPhone,
  faFilter,
  faSearch,
  faArchive,
  faUsers,
  faUserCheck,
  faCalendarMinus,
  faBuildingUser,
} from "@fortawesome/free-solid-svg-icons";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import StatsCard from "../common/StatsCard";
import StatusChip from "../common/StatusChip";
import EmptyState from "../common/EmptyState";
import { useConfirm } from "../ui";
const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const confirm = useConfirm();

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await fetch(`/api/employees`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Error fetching employees");
        }

        const data = await response.json();
        const activeEmployees = data.filter((e) => !e.archive);
        setEmployees(activeEmployees);
      } catch (err) {
        setError("Error fetching employees");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  const handleArchive = async (employeeId) => {
    const ok = await confirm({
      title: "Archive employee?",
      message: "The employee will be moved to the archived list and lose access.",
      confirmText: "Archive",
      destructive: true,
    });
    if (!ok) return;
    try {
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `/api/employees/${employeeId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ archive: true }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error archiving employee");
      }

      setEmployees((prev) => prev.filter((e) => e._id !== employeeId));
    } catch (err) {
      alert("Error archiving employee");
    }
  };

  const filteredEmployees = employees.filter((e) => {
    switch (filterType) {
      case "employeeid":
        return e.employeeid?.includes(filterQuery) || !filterQuery;
      case "name":
        return (
          e.name?.toLowerCase().includes(filterQuery.toLowerCase()) ||
          !filterQuery
        );
      case "managerName":
        return (
          e.managerName?.toLowerCase().includes(filterQuery.toLowerCase()) ||
          !filterQuery
        );
      case "position":
        return (
          e.role?.toLowerCase().includes(filterQuery.toLowerCase()) ||
          !filterQuery
        );
      default:
        return true;
    }
  });

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Employee Directory"
        subtitle="Manage and view all active employees"
        icon={<FontAwesomeIcon icon={faUsers} />}
      />

      {/* Stats Row */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            label="Total Employees"
            value={filteredEmployees.length}
            icon={<FontAwesomeIcon icon={faUsers} />}
            color="#667eea"
            bgColor="#EBF0FF"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            label="Active"
            value={employees.length}
            icon={<FontAwesomeIcon icon={faUserCheck} />}
            color="#059669"
            bgColor="#D1FAE5"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            label="On Leave"
            value={0}
            icon={<FontAwesomeIcon icon={faCalendarMinus} />}
            color="#D97706"
            bgColor="#FEF3C7"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            label="Departments"
            value={0}
            icon={<FontAwesomeIcon icon={faBuildingUser} />}
            color="#7C3AED"
            bgColor="#EDE9FE"
          />
        </Grid>
      </Grid>

      {/* Table Card */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #eef0f6",
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        {/* Search & Filter Bar */}
        <Box
          sx={{
            p: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            borderBottom: "2px solid #f0f0f5",
          }}
        >
          <TextField
            placeholder="Search employees..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon
                    icon={faSearch}
                    style={{ color: "#8b8fa3", fontSize: 14 }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 260,
              flex: 1,
              maxWidth: 360,
              "& .MuiOutlinedInput-root": {
                bgcolor: "#fff",
                borderRadius: "8px",
                fontSize: "0.875rem",
                "& fieldset": { borderColor: "#eef0f6" },
                "&:hover fieldset": { borderColor: "#CBD5E1" },
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #eef0f6",
              borderRadius: "8px",
              bgcolor: "#fff",
              px: 1.5,
              height: 40,
              minWidth: 200,
            }}
          >
            <FontAwesomeIcon
              icon={faFilter}
              style={{ color: "#8b8fa3", marginRight: 8, fontSize: 13 }}
            />
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              displayEmpty
              variant="standard"
              disableUnderline
              fullWidth
              sx={{ fontSize: "0.875rem", color: "#5a5f7a" }}
              MenuProps={{
                PaperProps: {
                  sx: { borderRadius: "8px", mt: 0.5, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" },
                },
              }}
            >
              <MenuItem value="">All Employees</MenuItem>
              <MenuItem value="employeeid">Employee ID</MenuItem>
              <MenuItem value="name">Employee Name</MenuItem>
              <MenuItem value="managerName">Manager Name</MenuItem>
              <MenuItem value="position">Position</MenuItem>
            </Select>
          </Box>
        </Box>

        {/* Content Area */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CircularProgress size={36} sx={{ color: "#667eea" }} />
            <Typography sx={{ mt: 2, color: "#8b8fa3", fontSize: "0.875rem" }}>
              Loading employees...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography sx={{ color: "#EF4444", fontWeight: 600, mb: 1 }}>
              Something went wrong
            </Typography>
            <Typography sx={{ color: "#8b8fa3", fontSize: "0.875rem", mb: 2 }}>
              {error}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.location.reload()}
              sx={{
                borderColor: "#667eea",
                color: "#667eea",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Try Again
            </Button>
          </Box>
        ) : filteredEmployees.length === 0 ? (
          <EmptyState
            title="No employees found"
            subtitle="Try adjusting your search or filter criteria"
            icon={<FontAwesomeIcon icon={faSearch} />}
          />
        ) : (
          <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  {["Employee", "Manager", "Status", "Contact", "Actions"].map(
                    (header) => (
                      <TableCell
                        key={header}
                        sx={{
                          bgcolor: "#f5f7fb",
                          color: "#5a5f7a",
                          fontWeight: 600,
                          fontSize: "0.8125rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          borderBottom: "2px solid #f0f0f5",
                          whiteSpace: "nowrap",
                          py: 1.75,
                        }}
                      >
                        {header}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.map((employee) => (
                  <TableRow
                    key={employee._id}
                    sx={{
                      "&:hover": { bgcolor: "#f5f7fb" },
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    {/* Employee */}
                    <TableCell sx={{ py: 2, fontSize: "0.875rem", color: "#1a1a2e" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          sx={{
                            background: "linear-gradient(135deg, #667eea, #764ba2)",
                            color: "#fff",
                            width: 36,
                            height: 36,
                            fontSize: "0.875rem",
                            fontWeight: 600,
                          }}
                        >
                          {employee.name?.charAt(0).toUpperCase() || "U"}
                        </Avatar>
                        <Box>
                          <Typography
                            sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e" }}
                          >
                            {employee.name}
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: "#8b8fa3" }}>
                            {employee.employeeid || "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Manager */}
                    <TableCell sx={{ py: 2, fontSize: "0.875rem", color: "#1a1a2e" }}>
                      {employee.managerName || "N/A"}
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={{ py: 2 }}>
                      <StatusChip status="active" />
                    </TableCell>

                    {/* Contact */}
                    <TableCell sx={{ py: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          style={{ color: "#8b8fa3", fontSize: 12 }}
                        />
                        <Typography sx={{ fontSize: "0.8125rem", color: "#5a5f7a" }}>
                          {employee.email}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <FontAwesomeIcon
                          icon={faPhone}
                          style={{ color: "#8b8fa3", fontSize: 12 }}
                        />
                        <Typography sx={{ fontSize: "0.8125rem", color: "#5a5f7a" }}>
                          {employee.contactNumber || "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={{ py: 2 }}>
                      <Tooltip title="Archive Employee" arrow>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleArchive(employee._id)}
                          startIcon={
                            <FontAwesomeIcon icon={faArchive} style={{ fontSize: 12 }} />
                          }
                          sx={{
                            borderRadius: "8px",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            textTransform: "none",
                            borderColor: "#eef0f6",
                            color: "#EF4444",
                            px: 1.5,
                            "&:hover": {
                              borderColor: "#EF4444",
                              bgcolor: "#FEF2F2",
                            },
                          }}
                        >
                          Archive
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </PageContainer>
  );
};

export default EmployeeList;
